import { useState, useEffect } from "react";
import { use_store } from "../store";

export const Loading = () => {
  const { t } = use_store();

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="text-[48px] text-white font-light">
        {t("Loading")}
        <span className="inline-block w-[80px]">
          <span
            className="inline-block animate-bounce"
            style={{ animationDelay: "0s" }}
          >
            .
          </span>
          <span
            className="inline-block animate-bounce"
            style={{ animationDelay: "0.1s" }}
          >
            .
          </span>
          <span
            className="inline-block animate-bounce"
            style={{ animationDelay: "0.2s" }}
          >
            .
          </span>
        </span>
      </div>
    </div>
  );
};

interface LoadingBoxProps {
  text: string;
  username?: string;
  mode?: string;
}

const get_analysis_steps = (t: (key: string) => string) => [
  t("Figuring out who they follow"),
  t("Looking at who follows them"),
  t("Figuring out who's new"),
  t("Looking for suspicious profiles"),
  t("Taking a closer look"),
  t("Looking through off-site content"),
  t("Analyzing behavior patterns"),
  t("Checking for red flags"),
  t("Wrapping up"),
];

const get_stories_steps = (t: (key: string) => string) => [
  t("Finding stories"),
  t("Loading story content"),
  t("Processing media"),
  t("Preparing anonymous view"),
  t("Finalizing"),
];

export const PaymentLoader = () => {
  const [dots, set_dots] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      set_dots((prev) => (prev % 3) + 1);
    }, 500);

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 6000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-surface flex items-center justify-center z-50">
      <div className="text-[92px] text-brand-2">{".".repeat(dots)}</div>
    </div>
  );
};

export const LoadingBox = ({ text, username, mode }: LoadingBoxProps) => {
  const [remaining_seconds, set_remaining_seconds] = useState(9);
  const [progress, set_progress] = useState(0);
  const [current_step, set_current_step] = useState(0);
  const { get_load_time, t } = use_store();
  const [steps, set_steps] = useState(
    mode == "stories" ? get_stories_steps(t) : get_analysis_steps(t),
  );

  useEffect(() => {
    set_steps(mode == "stories" ? get_stories_steps(t) : get_analysis_steps(t));
  }, [mode, t]);

  // Progress timer - runs independently
  useEffect(() => {
    const start_time = Date.now();
    const duration = get_load_time();
    set_current_step(0);

    const interval = setInterval(() => {
      const elapsed = Date.now() - start_time;
      const new_progress = Math.min((elapsed / duration) * 100, 100);
      const new_remaining = Math.max(
        Math.floor((duration - elapsed) / 1000),
        0,
      );

      set_progress(new_progress);
      set_remaining_seconds(new_remaining);

      if (elapsed >= duration) {
        clearInterval(interval);
      }
    }, 60);

    return () => clearInterval(interval);
  }, []); // Empty dependency array - only runs once

  // Step change timer - runs independently
  useEffect(() => {
    const change_step = () => {
      set_current_step((prev) => {
        if (prev < steps.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    };

    const duration = get_load_time();
    const interval = setInterval(change_step, duration / steps.length);

    return () => {
      clearInterval(interval);
    };
  }, [steps]); // Re-run when steps change

  return (
    <div className="w-full max-w-[640px] w-[calc(100vw-32px)] px-[8px] py-[16px] border-[1px] border-lines rounded-[4px]">
      {username && (
        <p className="text-center mb-[8px]  ">
          {t("Analyzing {username}").replace("{username}", `@${username}`)}
        </p>
      )}
      <p className="text-center mb-[16px]">{text}</p>
      <div className="relative w-full h-[4px] bg-lines overflow-hidden rounded-[4px]">
        <div
          className="absolute top-0 left-0 h-full bg-brand-2 transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-center mt-[16px] eyebrows">
        ...{steps[current_step].toUpperCase()} ({remaining_seconds}{" "}
        {t("seconds")})
      </p>
    </div>
  );
};
